import qs from 'query-string';
import featureFlags from 'src/featureFlags';
import constants from 'src/constants';
import env from 'src/env';

const {
  MANUFACTURER_LOGOS,
  MANUFACTURER_LOGOS_SMALL,
  MANUFACTURERS_WITH_LOGOS,
} = constants.logos;
const { PRODUCTION_PRODUCT_CATALOG_URLS } = constants.app;

/**
 * Build a query object for an api request.
 * Used as part of the request options object.
 * See the file src/lib/request.js.
 * @param {string} param
 * @param {string} value
 * @return {Object} query object
 */
const buildQuery = (param, value) => {
  const query = {};
  if (typeof param !== 'string' || param === '') return query;
  if (typeof value !== 'string' || value === '') return query;
  query[param] = value;
  return query;
};

const convertArrayToMapById = (arr) =>
  arr.reduce((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {});

const convertArrayToMapByIdRecursively = (arr) =>
  arr.reduce((acc, item) => {
    acc[item.id] = item;

    if (Array.isArray(item.children) && !!item.children.length) {
      acc = Object.assign(acc, convertArrayToMapByIdRecursively(item.children));
    }

    return acc;
  }, {});

/**
 *
 * @return {boolean} Whether to force the app to use the Production product
 * catalog
 */
const useProductionCatalog = () =>
  featureFlags.shouldUseProductionProductCatalog();

/**
 *
 * @param {Object} job - a job from hyperion (https://developers.hover.to/#jobs)
 * @param {string} type - either products, manufacturers, or manufacturerProducts
 */
const getProductCatalogUrl = (job, type) => {
  const srcKey = `${type}Src`;

  if (useProductionCatalog()) {
    return PRODUCTION_PRODUCT_CATALOG_URLS[srcKey];
  }

  const jobProductCatalogUrl = new URL(job.machete_blob.PlanetTerror[srcKey]);
  const pathname = jobProductCatalogUrl.pathname || '';
  const query = jobProductCatalogUrl.search || '';
  const hash = jobProductCatalogUrl.hash || '';

  return env.PRODUCT_CATALOG_URL + pathname + query + hash;
};

/**
 *
 * @param {*} job - Hyperion job API object
 * @returns array of Legacy Provider IDs to be used for showing products
 * Extracts provider IDs from job.machete_blob.PlanetTerror.manufacturersSrc
 * see https://github.com/hoverinc/hyperion/blob/master/app/helpers/machete_helper.rb#L51
 */
const getProviderIdsFromJob = (job) => {
  const url = new URL(job.machete_blob.PlanetTerror.manufacturersSrc);
  const query = qs.parse(url.search);
  const ids = query['provider_ids[]'] || [];
  return ids.map((id) => Number(id));
};

const convertManufacturersResponseData = (responseData) => {
  return responseData.map((item) => {
    const itemId = item.id;
    const result = {
      name: item.name,
      id: itemId,
    };

    if (MANUFACTURERS_WITH_LOGOS.includes(itemId)) {
      result.logo_url = MANUFACTURER_LOGOS[itemId];
      result.sm_logo_url = MANUFACTURER_LOGOS_SMALL[itemId];
    }

    return result;
  });
};

const convertTexturesResponseData = (responseData) => {
  return responseData.map((texture) => {
    const {
      name: textureName,
      legacyId: id,
      width,
      height,
      repeatable,
      hasTransparency: transparent,
      bumpMapResized,
      diffuseMapThumbnail,
      diffuseMapResized,
      normalMapResized,
      roughMapResized,
      basecolorMapResized,
      aoMapResized,
      metalMapResized,
      pbrMapResized,
    } = texture || {};

    const result = {
      id,
      width,
      height,
      transparent,
      repeatable,
      texture: {
        diffuse_map: {
          url: diffuseMapResized && diffuseMapResized.redirectUrl,
        },
        thumbnail: {
          url: diffuseMapThumbnail && diffuseMapThumbnail.redirectUrl,
        },
      },
    };

    [
      ['bump_map', bumpMapResized],
      ['normal_map', normalMapResized],
      ['rough_map', roughMapResized],
      ['basecolor_map', basecolorMapResized],
      ['ao_map', aoMapResized],
      ['metal_map', metalMapResized],
      ['pbr_map', pbrMapResized],
    ].forEach(([key, value]) => {
      if (value && value.redirectUrl) {
        result[key] = {
          resized: {
            url: value.redirectUrl,
          },
        };
      }
    });

    return result;
  });
};

export default {
  convertManufacturersResponseData,
  convertTexturesResponseData,
  buildQuery,
  convertArrayToMapById,
  convertArrayToMapByIdRecursively,
  getProductCatalogUrl,
  getProviderIdsFromJob,
};
